/**
 * Roadmap branches + verdicts — metadata module.
 *
 * Roadmap content is sourced from Supabase (`features` + `feature_roadmap_verdicts`
 * tables). This file holds only the static labels, colors, and helpers the UI
 * needs to render the two boards:
 *
 *   * `/roadmaps/all-features`  — triage board (mvp / non-mvp)
 *   * `/roadmaps/from-scratch`  — MVP build-strategy board
 *                                  (off-the-shelf / build-on-top / from-scratch)
 *
 * Feature grouping / pills / colours use the unified domain taxonomy from
 * `./domains.ts`, shared with the architecture map.
 */

import type {
  FeatureDomain,
  RoadmapBranchValue,
  VerdictValue,
} from "@/infrastructure/supabase/database.types";

// =============================================================================
// Branches
// =============================================================================

export const ROADMAP_BRANCHES = ["all-features", "from-scratch"] as const;
export type RoadmapBranch = (typeof ROADMAP_BRANCHES)[number];

export const ROADMAP_LABEL: Record<RoadmapBranch, string> = {
  "all-features": "All Features",
  "from-scratch": "MVP board",
};

export const ROADMAP_TAGLINE: Record<RoadmapBranch, string> = {
  "all-features": "Triage every feature into MVP or Non-MVP.",
  "from-scratch": "Classify MVP features as off-the-shelf, build-on-top, or from-scratch.",
};

export const ROADMAP_COLOR: Record<RoadmapBranch, string> = {
  "all-features": "oklch(0.55 0.17 190)", // brand cyan
  "from-scratch": "oklch(0.62 0.22 25)", // red (greenfield / new build)
};

export const ROADMAP_COLOR_SOFT: Record<RoadmapBranch, string> = {
  "all-features": "oklch(0.94 0.06 190)",
  "from-scratch": "oklch(0.94 0.06 25)",
};

export function isRoadmapBranch(value: string): value is RoadmapBranch {
  return (ROADMAP_BRANCHES as readonly string[]).includes(value);
}

// =============================================================================
// Verdicts
// =============================================================================

export const ALL_FEATURES_VERDICTS = ["mvp", "non-mvp"] as const;
export const MVP_BOARD_VERDICTS = ["off-the-shelf", "build-on-top", "from-scratch"] as const;

export type AllFeaturesVerdict = (typeof ALL_FEATURES_VERDICTS)[number];
export type MvpBoardVerdict = (typeof MVP_BOARD_VERDICTS)[number];
export type Verdict = VerdictValue;

export const VERDICT_LABEL: Record<Verdict, string> = {
  mvp: "MVP",
  "non-mvp": "Non-MVP",
  "off-the-shelf": "Off-the-shelf",
  "build-on-top": "Build on top",
  "from-scratch": "From scratch",
};

export const VERDICT_DESCRIPTION: Record<Verdict, string> = {
  mvp: "In scope for the B2B MVP.",
  "non-mvp": "Out of scope for the B2B MVP — defer or drop.",
  "off-the-shelf": "Adopt a third-party product as-is — no customization.",
  "build-on-top": "Adopt a third-party product, then customize it — base is a new vendor.",
  "from-scratch": "Greenfield build with no carry-forward base — fully new.",
};

export const VERDICT_COLOR: Record<Verdict, string> = {
  mvp: "oklch(0.55 0.17 190)", // brand cyan — in scope
  "non-mvp": "oklch(0.62 0.02 240)", // neutral slate — out of scope
  "off-the-shelf": "oklch(0.55 0.17 290)", // violet
  "build-on-top": "oklch(0.62 0.16 50)", // orange
  "from-scratch": "oklch(0.62 0.22 25)", // red (new build)
};

export const VERDICT_COLOR_SOFT: Record<Verdict, string> = {
  mvp: "oklch(0.94 0.06 190)",
  "non-mvp": "oklch(0.94 0.005 240)",
  "off-the-shelf": "oklch(0.94 0.05 290)",
  "build-on-top": "oklch(0.94 0.05 50)",
  "from-scratch": "oklch(0.94 0.06 25)",
};

export function verdictsForBranch(branch: RoadmapBranch): readonly Verdict[] {
  return branch === "all-features" ? ALL_FEATURES_VERDICTS : MVP_BOARD_VERDICTS;
}

export function isVerdictForBranch(branch: RoadmapBranch, value: string): value is Verdict {
  return (verdictsForBranch(branch) as readonly string[]).includes(value);
}

// =============================================================================
// Re-export Supabase enum types so consumers can import from one place
// =============================================================================

export type { FeatureDomain, RoadmapBranchValue, VerdictValue };
